import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Mic, Sparkles, FlaskConical } from "lucide-react";
import PublicVoiceDemoDialog from "@/components/PublicVoiceDemoDialog";

/* Global "Talk to Our AI Employee" launcher.
 *  – Renders a floating pill CTA in the bottom-right after the visitor
 *    scrolls past the hero (≥ 40% of the first viewport).
 *  – Also exposes a global `window.openAiDemo()` hook so Hero and any
 *    other section can trigger the modal.
 *  – Switches to the LitLab variant (Iris) when the visitor is on /litlab/*.
 */
export default function TalkToAiEmployeeLauncher() {
  const [open, setOpen] = useState(false);
  const [showPill, setShowPill] = useState(false);
  const location = useLocation();
  const isLitLab = (location.pathname || "").startsWith("/litlab");
  const isQst    = (location.pathname || "").startsWith("/qst");
  const variant = isLitLab ? "litlab" : "websitetech";

  useEffect(() => {
    // Expose global trigger for Hero button + any inline CTA
    window.openAiDemo = () => setOpen(true);
    const onScroll = () => {
      const y = window.scrollY;
      const threshold = Math.min(window.innerHeight * 0.4, 400);
      setShowPill(y > threshold);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      delete window.openAiDemo;
    };
  }, []);

  // Close the dialog when the visitor navigates between LitLab / QST / main
  // so we don't hand a WebsiteTech-scoped session to a client-site visitor.
  useEffect(() => { setOpen(false); }, [isLitLab, isQst]);

  // QST is a client site — it has its own docked assistant.
  // Never show the WebsiteTech-branded launcher there.
  if (isQst) return null;

  const pillGradient = isLitLab
    ? "from-sky-600 via-sky-500 to-emerald-500"
    : "from-indigo-600 via-indigo-500 to-fuchsia-500";
  const pillLabel = isLitLab ? "Ask Iris — L.I.T. Labs AI" : "Talk to our AI Employee";
  const pillShort = isLitLab ? "Ask Iris" : "Try AI";
  const PillIcon = isLitLab ? FlaskConical : Mic;

  return (
    <>
      {showPill && !open && (
        <button
          type="button"
          data-testid={isLitLab ? "litlab-ai-pill" : "talk-ai-pill"}
          onClick={() => setOpen(true)}
          className={`fixed z-[900] bottom-6 right-6 group flex items-center gap-2 rounded-full pl-3 pr-5 py-3 bg-gradient-to-r ${pillGradient} text-white font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all`}
        >
          <span className="w-8 h-8 rounded-full bg-white/20 grid place-items-center relative">
            <PillIcon className="w-4 h-4" />
            <span className="absolute inset-0 rounded-full ring-2 ring-white/50 animate-ping" />
          </span>
          <span className="hidden sm:inline">{pillLabel}</span>
          <span className="sm:hidden">{pillShort}</span>
          <span className="ml-1 text-[10px] font-normal uppercase tracking-widest bg-white/20 rounded px-1.5 py-0.5">Live</span>
        </button>
      )}
      <PublicVoiceDemoDialog open={open} onClose={() => setOpen(false)} variant={variant} />
    </>
  );
}

/* Standalone inline button — style-consistent Hero CTA. */
export function TalkToAiEmployeeButton({ className = "", size = "md", variant = "primary" }) {
  const base = "inline-flex items-center gap-2 rounded-xl font-semibold transition-all";
  const sizes = { sm: "px-4 py-2 text-sm", md: "px-5 py-3 text-sm", lg: "px-6 py-3.5 text-base" };
  const variants = {
    primary: "bg-gradient-to-r from-indigo-600 via-indigo-500 to-fuchsia-500 text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40 hover:scale-[1.02]",
    ghost: "bg-white text-slate-900 border border-slate-200 hover:border-indigo-400 hover:text-indigo-700",
    dark: "bg-slate-900 text-white hover:bg-slate-800",
  };
  const onClick = () => {
    if (typeof window !== "undefined" && typeof window.openAiDemo === "function") {
      window.openAiDemo();
    }
  };
  return (
    <button
      type="button"
      data-testid="talk-ai-btn"
      onClick={onClick}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
    >
      <Sparkles className="w-4 h-4" />
      Talk to our AI Employee
      <span className="text-[10px] font-normal uppercase tracking-widest bg-white/20 rounded px-1.5 py-0.5 ml-1">Live</span>
    </button>
  );
}
